import AutoModeOutlined from '@mui/icons-material/AutoModeOutlined';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { type ReactElement, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router';

import type { AutomaticModeSuggestion } from '../../api';

import { homeLinks } from './links';

/**
 * Two weeks without answers in confirmation mode: suggest automatic mode, with no penalty
 * (FXM-02 §6 case 11; M1 R11). I7: only a link to E7; the mode changes only when the trader
 * validates new settings there.
 */
export function AutomaticModeSuggestions({
  suggestions,
}: {
  readonly suggestions: readonly AutomaticModeSuggestion[];
}): ReactElement {
  return (
    <>
      {suggestions.map((suggestion) => (
        <Suggestion key={suggestion.subscriptionId} suggestion={suggestion} />
      ))}
    </>
  );
}

function Suggestion({ suggestion }: { suggestion: AutomaticModeSuggestion }): ReactElement {
  const { t } = useTranslation();
  const titleId = useId();
  return (
    <Paper
      variant="outlined"
      component="section"
      aria-labelledby={titleId}
      sx={(theme) => ({
        p: 1.75,
        display: 'flex',
        gap: 1.5,
        bgcolor: theme.fx.infoBg,
        borderColor: theme.fx.info,
      })}
    >
      <AutoModeOutlined aria-hidden color="primary" />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
        <Typography id={titleId} component="h2" sx={{ fontWeight: 600, fontSize: 15 }}>
          {t('home.suggestion.title')}
        </Typography>
        <Typography variant="body2">
          {t('home.suggestion.body', { channel: suggestion.channelName })}
        </Typography>
        <Link
          component={RouterLink}
          to={homeLinks.channelSettings(suggestion.subscriptionId)}
          sx={{ fontSize: 14, fontWeight: 600, alignSelf: 'flex-start' }}
        >
          {t('home.suggestion.action')}
        </Link>
      </Box>
    </Paper>
  );
}
